/**
 * RBAC API service
 * Handles all role and permission-related API calls
 */

import { apiFetch } from './index';

export interface Permission {
  permissionId: number;
  permissionName: string;
  permissionDescription: string;
}

export interface Role {
  roleId: number;
  roleName: string;
  roleDescription: string;
  isActive: boolean;
  isSystem?: boolean;
  permissions?: Permission[];
  userCount?: number;
  createdAt: string;
  modifiedAt?: string;
}

export interface RoleCreateData {
  roleName: string;
  roleDescription: string;
  isActive?: boolean;
  permissions?: number[]; // Array of permissionIds
}

export interface RoleUpdateData {
  roleName?: string;
  roleDescription?: string;
  isActive?: boolean;
  permissions?: number[]; // Array of permissionIds (for system role permission updates)
}

export interface RolePermissionUpdate {
  permissions: number[]; // Array of permissionIds
}

export interface UserRoleAssignment {
  userId: number;
  roleId: number;
  reason?: string;
}

export interface BulkRoleAssignment {
  userIds: number[];
  roleId: number;
  reason?: string;
}

export interface RoleUser {
  userId: number;
  personId: number;
  personName: string;
  email: string;
  organizationName?: string;
  assignedAt: string;
}

export const rbacApi = {
  // List all permissions
  async listPermissions(params?: { category?: string; search?: string }): Promise<Permission[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });
    }

    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<Permission[]>(
      `/rbac/permissions${queryParams.toString() ? `?${queryParams}` : ''}`
    );
  },

  // Get permissions grouped by category
  async listPermissionsGrouped(): Promise<Record<string, Permission[]>> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<Record<string, Permission[]>>(`/rbac/permissions/grouped`);
  },

  // List all roles
  async listRoles(params?: {
    includeInactive?: boolean;
    includePermissions?: boolean;
    includeUserCount?: boolean;
  }): Promise<Role[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.includeInactive !== undefined) {
        queryParams.append('includeInactive', params.includeInactive.toString());
      }
      if (params.includePermissions !== undefined) {
        queryParams.append('includePermissions', params.includePermissions.toString());
      }
      if (params.includeUserCount !== undefined) {
        queryParams.append('includeUserCount', params.includeUserCount.toString());
      }
    }

    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<Role[]>(
      `/rbac/roles${queryParams.toString() ? `?${queryParams}` : ''}`
    );
  },

  // Get a single role by ID
  async getRole(roleId: number, includePermissions: boolean = true): Promise<Role> {
    const queryParams = new URLSearchParams();
    queryParams.append('includePermissions', includePermissions.toString());

    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<Role>(
      `/rbac/roles/${roleId}?${queryParams}`
    );
  },

  // Create a new role
  async createRole(data: RoleCreateData): Promise<Role> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<Role>(`/rbac/roles`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Update a role
  async updateRole(roleId: number, data: RoleUpdateData): Promise<Role> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<Role>(`/rbac/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Delete a role
  async deleteRole(roleId: number): Promise<void> {
    // apiFetch already handles errors and returns parsed JSON
    await apiFetch<void>(`/rbac/roles/${roleId}`, {
      method: 'DELETE'
    });
  },

  // Update role permissions
  async updateRolePermissions(roleId: number, permissions: number[]): Promise<Role> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<Role>(
      `/rbac/roles/${roleId}/permissions`,
      {
        method: 'POST',
        body: JSON.stringify({ permissions })
      }
    );
  },

  // Get users assigned to a role
  async getRoleUsers(
    roleId: number,
    params?: {
      page?: number;
      pageSize?: number;
      search?: string;
    }
  ): Promise<{
    users: RoleUser[];
    totalCount: number;
    page: number;
    pageSize: number;
  }> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page || 1).toString());
    queryParams.append('pageSize', (params?.pageSize || 50).toString());
    if (params?.search) {
      queryParams.append('search', params.search);
    }

    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<{
      users: RoleUser[];
      totalCount: number;
      page: number;
      pageSize: number;
    }>(`/rbac/roles/${roleId}/users?${queryParams}`);
  },

  // Assign a role to a user
  async assignRole(data: UserRoleAssignment): Promise<{ message: string }> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<{ message: string }>(`/rbac/roles/assign`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Bulk assign a role to multiple users
  async bulkAssignRole(data: BulkRoleAssignment): Promise<{
    message: string;
    successCount: number;
    failedCount: number;
  }> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<{
      message: string;
      successCount: number;
      failedCount: number;
    }>(`/rbac/roles/bulk-assign`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
};